// @flow
import * as React from "react"
import _ from "lodash"
import RaisedButton from "material-ui/RaisedButton"
import withState from "recompose/withState"
import TextField from "material-ui/TextField"
import {Toolbar, ToolbarGroup, ToolbarTitle} from "material-ui/Toolbar"
import IconButton from "material-ui/IconButton"
import MenuIcon from "material-ui/svg-icons/navigation/menu"
import HelpIcon from "material-ui/svg-icons/action/help-outline"
import * as Util from "../common"
import {Card, CardText, CardTitle} from "material-ui/Card"
import Drawer from "material-ui/Drawer"
import UnitNavView from "./UnitNavView"
import {containers} from "@swarm2/core"

const M = Util.intlMessages("view.UnitView")
const UM = Util.intlMessages("units")
const UnitDescription = Util.prefixedDescription("units")

function ResourceItem({name, count}) {
  // show decimals for small on-whole numbers.
  const maxSmall = count.floor().equals(count) ? undefined : 10
  return (
    <M
      id="resource.item"
      values={{
        count: <Util.Num val={count} maxSmall={maxSmall} />,
        unittype: <UM id={name + ".plural"} values={{count}} />,
      }}
    />
  )
}
function ResourceList({items}) {
  return (
    <M
      id="resource.list"
      values={{
        length: items.length,
        first: items[0],
        last: items[items.length - 1],
        // item => "item, " for all but the last item
        head: (
          <span>
            {items
              .slice(0, -1)
              .map((item, i) => (
                <M key={i} id="resource.listitem" values={{item}} />
              ))}
          </span>
        ),
      }}
    />
  )
}
function Resources({items}) {
  return (
    <ResourceList
      items={_.map(items, (count, name) => (
        <ResourceItem name={name} count={count} />
      ))}
    />
  )
}

const UnitTextRender = ({
  unitName,
  prodEach,
  prodTotal,
  costEach,
  isProducible,
  count,
  velocity,
}) => (
  <div>
    <p>
      <UnitDescription id={unitName} />
    </p>
    <p>
      <M
        id="body.count"
        values={{
          count: <Util.Num val={count} sigfigs={5} />,
          name: <UM id={unitName + ".plural"} values={{count}} />,
        }}
      />
    </p>
    {Object.keys(prodEach).length ? (
      <div>
        <p>
          <M
            id="body.prodEach"
            values={{
              resources: <Resources items={prodEach} />,
              name: <UM id={unitName + ".plural"} values={{count: 1}} />,
            }}
          />
        </p>
        <p>
          <M
            id="body.prodTotal"
            values={{
              resources: <Resources items={prodTotal} />,
              name: <UM id={unitName + ".plural"} values={{count: 1}} />,
            }}
          />
        </p>
      </div>
    ) : null}
    {isProducible ? (
      <div>
        <p>
          <M
            id="body.velocity"
            values={{
              velocity: <Util.Num val={velocity} />,
              name: <UM id={unitName + ".plural"} values={{count: velocity}} />,
            }}
          />
        </p>
      </div>
    ) : null}
    {Object.keys(costEach).length ? (
      <div>
        <p>
          <M
            id="body.costeach"
            values={{
              unitName: <UM id={unitName + ".plural"} values={{count: 1}} />,
              cost: <Resources items={costEach} />,
            }}
          />
        </p>
      </div>
    ) : null}
  </div>
)

function BuyNUnitsRender(props) {
  const {
    unitName,
    input,
    setBuyN,
    buyCount,
    isBuyButtonDisabled,
    buyUnit,
  } = props
  return (
    <span>
      <TextField
        value={input}
        hintText={""}
        floatingLabelText={<Util.Num val={buyCount} />}
        onChange={e => setBuyN(e.target.value, unitName)}
      />
      {isBuyButtonDisabled ? (
        <RaisedButton primary disabled label={<M id="BuyN.cantbuy" />} />
      ) : (
        <RaisedButton
          primary
          onClick={buyUnit}
          label={
            <M
              id="BuyN.buybutton"
              values={{
                name: (
                  <UM id={unitName + ".plural"} values={{count: buyCount}} />
                ),
              }}
            />
          }
        />
      )}
      <div>
        <RaisedButton
          secondary
          onClick={() => setBuyN("", unitName)}
          label="1"
        />
        <RaisedButton
          secondary
          onClick={() => setBuyN("25%", unitName)}
          label="25%"
        />
        <RaisedButton
          secondary
          onClick={() => setBuyN("100%", unitName)}
          label="100%"
        />
      </div>
    </span>
  )
}

const BuyUnitFormRender = withState(
  "open",
  "setOpen",
  false,
)(({open, setOpen, ...props}) => {
  return (
    <div>
      <IconButton onClick={() => setOpen(!open)}>
        <HelpIcon />
      </IconButton>
      <BuyNUnitsRender {...props} />
      <Card style={{display: open ? "" : "none"}}>
        <CardTitle>
          <M id="BuyN.help.title" />
        </CardTitle>
        <CardText>
          <p>
            <M id="BuyN.help0" />
          </p>
          <p>
            <M id="BuyN.help1" />
          </p>
          <p>
            <M id="BuyN.help2" />
          </p>
          <p>
            <M id="BuyN.help3" />
          </p>
        </CardText>
      </Card>
    </div>
  )
})

function UnitToolbarRender({unitName, displayName, drawerProps}) {
  return (
    <Toolbar>
      <ToolbarGroup>
        <ToolbarTitle
          text={
            <M
              id="title"
              values={{
                charName: displayName,
                unitName: <UM id={unitName + ".plural"} values={{count: 0}} />,
              }}
            />
          }
        />
      </ToolbarGroup>
      <ToolbarGroup>
        {drawerProps.docked ? null : (
          <IconButton onClick={() => drawerProps.setOpen(!drawerProps.open)}>
            <MenuIcon />
          </IconButton>
        )}
      </ToolbarGroup>
    </Toolbar>
  )
}

function UnitRender(props) {
  if (props.isError) return <Util.Error error={props} />
  const {
    charId,
    unitName,
    readonly,
    isBuyable,
    drawerProps,
    drawerStyle,
  } = props
  return (
    <div>
      <UnitToolbarRender
        drawerProps={drawerProps}
        unitName={unitName}
        displayName={props.displayName}
      />
      <CardText
        style={{paddingRight: drawerProps.docked ? "256px" : "inherit"}}
      >
        <Drawer {...drawerProps} {...drawerStyle} openSecondary={true}>
          <UnitNavView
            charId={charId}
            unitName={unitName}
            input={props.input}
            readonly={readonly}
          />
        </Drawer>
        <UnitTextRender
          unitName={unitName}
          {..._.pick(props, [
            "prodEach",
            "prodTotal",
            "costEach",
            "count",
            "velocity",
            "isProducible",
          ])}
        />
        {isBuyable && !readonly ? (
          <BuyUnitFormRender
            unitName={unitName}
            {..._.pick(props, [
              "input",
              "setBuyN",
              "buyCount",
              "isBuyButtonDisabled",
              "buyUnit",
            ])}
          />
        ) : null}
      </CardText>
    </div>
  )
}

export const UnitView = containers.units.Unit(Util.DumpProps(UnitRender))



// WEBPACK FOOTER //
// ./src/units/UnitView.js